import { BadgeAlert, ExternalLink, Trash2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { useCallback, useEffect, useId, useMemo } from "react"
import {
  createGetSteppedColour,
  GetSteppedColours,
} from "@/constants/priorityColours"
import { useScroller } from "@/components/shared/scroller"
import { AdjustableRanking } from "@/components/shared/adjustable-ranking"
import { ROUTES } from "@/constants"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayDay,
  displayPlainTime,
} from "@/lib/timetableZonedDatetime"
import { Separator } from "@/components/ui/separator"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { buttonVariants } from "@/components/ui/button-variants"
import { cn } from "@/lib/utils"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import {
  CalendarCard,
  CalendarSidebarLayout,
} from "@/components/shared/calendar-layout"
import { PreferenceCalendar } from "@/components/learners/preferences/preference-calendar"
import { ClassPreferenceCard } from "@/components/learners/preferences/class-preference-card"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  PageStore,
  selectCalendarEvents,
  selectPreferenceErrors,
  usePageDispatch,
  usePageSelector,
} from "./store"
import {
  associatePriorityWithPreference,
  dataLoaded,
  demotePreferenceRank,
  dissociatePriorityWithPreference,
  promotePreferenceRank,
  removePreference,
} from "./store/actions"
import {
  LearnersPreferences,
  Preference,
  PreferenceId,
  Priority,
  PriorityId,
  Subject,
  ValidPreference,
} from "./store/types"
import {
  preferencesActions,
  preferencesSelectors,
} from "./store/preferences-slice"
import { subjectSelectors } from "./store/subjects-slice"
import { eventsActions, eventsSelectors } from "./store/events-slice"
import { prioritiesSelectors } from "./store/priorities-slice"
import { Errors } from "./errors"
import {
  ShortlistSheet,
  type ShortlistSheetProps,
} from "@/components/learners/preferences/shortlist-sheet"

type PreferenceProps = {
  preferences: Array<LearnersPreferences>
  store: PageStore
  addedPreferenceId: number | null
  /** Receives the class chosen from a shortlist; the host adds the preference. */
  onSelectClass: ShortlistSheetProps["onSelect"]
}

export function Preferences({
  preferences,
  store,
  addedPreferenceId,
  onSelectClass,
}: PreferenceProps) {
  useEffect(() => {
    store.dispatch(dataLoaded(preferences))
    store.dispatch(
      preferencesActions.preferenceSelected(
        addedPreferenceId as PreferenceId | null,
      ),
    )
  }, [store, preferences, addedPreferenceId])
  const { t } = useTranslation(["preferences"])

  const subjects = usePageSelector(subjectSelectors.selectSubjects)

  const getColours = useMemo(() => createGetSteppedColour(), [])
  const buildEventTitle = useCallback(
    (priority: number, preference: number, subject_code: string) => {
      return t("timetable.events.short_title_all_subjects", {
        priority,
        preference,
        subject_code,
      })
    },
    [t],
  )

  const events = usePageSelector((state) =>
    selectCalendarEvents(state, {
      getColours,
      buildEventTitle,
    }),
  )
  const { cycleLength, calendarTimezoneId, startTemporalInstant } =
    usePageSelector(eventsSelectors.selectCalendar)
  const [calendarRef] = useFullCalendarApi("EditStudentPreferenceAssignment")

  return (
    <CalendarSidebarLayout>
      <ClassPreferenceCard>
        <div className="bg-background sticky top-0 z-10 h-12 w-full space-y-2">
          <ShortlistSheet
            onSelect={onSelectClass}
            trigger={
              <Button className="bg-background w-full">
                {t("edit.class.add")}
              </Button>
            }
          />

          <Separator />
        </div>
        <ul className="flex flex-col gap-4 pt-4">
          {subjects.map((subject) => (
            <SubjectPreference
              key={subject.id}
              subject={subject}
              getColours={getColours}
            />
          ))}
        </ul>
      </ClassPreferenceCard>
      <CalendarCard>
        <PreferenceCalendar
          events={events}
          cycleLength={cycleLength}
          calendarRef={calendarRef}
          calendarStartInstant={startTemporalInstant}
          calendarTimezoneId={calendarTimezoneId}
          eventContent={({
            event: {
              title,
              extendedProps: {
                preferenceId,
                priorityId,
                timetableSessionIndex,
              },
            },
          }) => {
            return (
              <EventWrapper
                preferenceId={preferenceId}
                title={title}
                priorityId={priorityId}
                timetableSessionIndex={timetableSessionIndex}
              />
            )
          }}
        />
      </CalendarCard>
      <PreferenceSheet />
    </CalendarSidebarLayout>
  )
}

function SubjectPreference({
  subject,
  getColours,
}: {
  subject: Subject
  getColours: GetSteppedColours
}) {
  const { textColour, bgColour } = getColours(String(subject.id), 0)
  const labelId = useId()
  const { name, code, preferenceIds } = subject
  const preferences = usePageSelector((state) =>
    preferencesSelectors.selectSubjectPreferences(state, {
      subjectId: subject.id,
    }),
  )

  if (preferenceIds.length === 0) {
    return null
  }

  return (
    <li className="space-y-2" aria-labelledby={labelId}>
      <div className="bg-background sticky top-12.5 z-9 items-center justify-between gap-2">
        <span
          className={`flex h-fit rounded px-2 py-1 text-sm ${bgColour} ${textColour}`}
          id={labelId}
        >
          {name} - ({code})
        </span>
      </div>
      <ol className="grid grid-cols-1 gap-4">
        {preferences.map((preference) => (
          <ClassPreference
            key={preference.id}
            preferencesCount={subject.preferenceIds.length}
            preference={preference}
            getColours={getColours}
          />
        ))}
      </ol>
    </li>
  )
}

function ClassPreference({
  preference,
  getColours,
  preferencesCount,
}: {
  preference: Preference
  getColours: GetSteppedColours
  preferencesCount: number
}) {
  const { t } = useTranslation(["preferences", "common"])
  const dispatch = usePageDispatch()
  const { setScrollNode } = useScroller()

  const { cardBorderColour, textColour, bgColour } = getColours(
    String(preference.subjectId),
    preference.rank - 1,
  )

  return (
    <li
      className={`flex flex-col gap-2 rounded-md border border-l-4 border-gray-200 p-4 shadow-sm ${cardBorderColour}`}
      aria-label={t("edit.class.preference.label", { rank: preference.rank })}
      ref={setScrollNode}
      onPointerEnter={() =>
        dispatch(eventsActions.preferenceHovered(preference.id))
      }
      onPointerLeave={() => dispatch(eventsActions.preferenceHovered(null))}
    >
      <div className="flex flex-row items-center justify-between">
        <span
          className={`flex h-fit w-fit rounded px-2 py-1 text-xs ${bgColour} ${textColour}`}
        >
          {t("edit.class.preference.label", { rank: preference.rank })}
        </span>
        <div
          className="flex flex-row items-center justify-between"
          data-testid="preference-actions"
        >
          <PreferenceRanking
            preference={preference}
            length={preferencesCount}
          />
          <Button
            variant={"destructive-outline"}
            className="h-fit w-fit p-2"
            size={"icon"}
            onClick={async () =>
              await dispatch(removePreference({ preference }))
            }
            aria-label={t("common:actions.remove")}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
      {preference.classDocument !== null ? (
        <ValidPreferenceBody preference={preference} />
      ) : (
        <Alert variant={"destructive"}>
          <BadgeAlert className="size-4" />
          <AlertTitle>{t("edit.preference.invalid.title")}</AlertTitle>
          <AlertDescription className="font-normal">
            {t("edit.preference.invalid.description")}
          </AlertDescription>
        </Alert>
      )}
    </li>
  )
}

function ValidPreferenceBody({ preference }: { preference: ValidPreference }) {
  const { t } = useTranslation(["preferences", "common"])
  const dispatch = usePageDispatch()
  const selectedPreference = usePageSelector(
    preferencesSelectors.selectSelectedPreference,
  )
  const selected = selectedPreference
    ? selectedPreference.id === preference.id
    : false

  const selectPreference = () => {
    if (selected) {
      return dispatch(preferencesActions.preferenceSelected(null))
    }

    dispatch(preferencesActions.preferenceSelected(preference.id))
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-1">
        <div className="flex flex-col text-xs">
          <span className="text-muted-foreground">{t("edit.class.label")}</span>
          <a
            className="line-clamp-2 text-sm underline hover:font-medium"
            href={ROUTES.SEARCH_CLASSES_RESULT_PATH(
              preference.esClassListingId,
            )}
            target="_blank"
          >
            {preference.classDocument.name}
            <span className="inline-flex items-center whitespace-nowrap">
              &nbsp;
              <ExternalLink className="size-4" />
            </span>
          </a>
        </div>
        <div className="flex flex-col text-xs">
          <span className="text-muted-foreground">
            {t("edit.class.educator.label")}
          </span>
          <span className="line-clamp-1 text-sm">
            {preference.educatorName}
          </span>
        </div>
      </div>
      <Button
        className="flex w-full gap-1 overflow-hidden"
        onClick={selectPreference}
      >
        <span className="flex-1 truncate text-left">
          {selected
            ? t("edit.class.preference.schedule.close")
            : preference.priorities.length > 0
              ? t("edit.class.preference.schedule.update")
              : t("edit.class.preference.schedule.select")}
        </span>
        <span className="shrink-0 text-xs">
          {preference.priorities.length}/
          {preference.classDocument.timetablePriorities.length}
        </span>
      </Button>
    </>
  )
}

function PreferenceRanking({
  preference,
  length,
}: {
  preference: Preference
  length: number
}) {
  const dispatch = usePageDispatch()
  const { t } = useTranslation("preferences")
  const handleAdjustPriority = async (direction: "increase" | "decrease") => {
    if (direction === "increase") {
      await dispatch(promotePreferenceRank({ preference }))
    } else {
      await dispatch(demotePreferenceRank({ preference }))
    }
  }

  // only item - can't move up or down
  if (length === 1) {
    return <div></div>
  }

  // can do both
  return (
    <div>
      <AdjustableRanking
        increaseButtonProps={{
          className: "p-1 w-fit h-fit ",
          onClick: () => handleAdjustPriority("increase"),
          disabled: preference.rank === 1,
          "aria-label":
            preference.rank === 1
              ? t("edit.class.preference.ranking.no_increase")
              : t("edit.class.preference.ranking.increase"),
        }}
        decreaseButtonProps={{
          className: "p-1 w-fit h-fit ",
          onClick: () => handleAdjustPriority("decrease"),
          disabled: preference.rank === length,
          "aria-label":
            preference.rank === length
              ? t("edit.class.preference.ranking.no_decrease")
              : t("edit.class.preference.ranking.decrease"),
        }}
      />
    </div>
  )
}

function TimetableSession({
  day,
  start_time,
  duration,
  eventTimezoneId,
}: {
  day: number
  start_time: string
  duration: number
  eventTimezoneId: string
}) {
  const { calendarTimezoneId, startTemporalZonedDateTime } = usePageSelector(
    eventsSelectors.selectCalendar,
  )
  const startTime = calculateEventStartCalendarTimezoneCoerced({
    startTime: start_time,
    day: day,
    calendarTimezoneId,
    eventTimezoneId,
    startTemporalZonedDateTime,
  })
  const { t } = useTranslation("preferences")

  return (
    <div>
      {t("timetable.schedule.fixed", {
        day: displayDay(day),
        start: displayPlainTime(startTime.toPlainTime()),
        end: displayPlainTime(
          startTime.add({ minutes: duration }).toPlainTime(),
        ),
      })}
    </div>
  )
}

interface Event {
  title: string
  priorityId: PriorityId
  preferenceId: PreferenceId
  timetableSessionIndex: number
}
function EventWrapper({ preferenceId, ...rest }: Event) {
  const preference = usePageSelector((state) =>
    preferencesSelectors.selectPreference(state, preferenceId),
  )

  if (!preference) return null
  if (preference.classDocument === null) return null

  return <Event {...rest} preference={preference} />
}

function Event({
  title,
  priorityId,
  preference,
  timetableSessionIndex,
}: Omit<Event, "preferenceId"> & { preference: ValidPreference }) {
  const { calendarTimezoneId, startTemporalZonedDateTime } = usePageSelector(
    eventsSelectors.selectCalendar,
  )

  const priority = usePageSelector((state) =>
    prioritiesSelectors.selectPriority(state, priorityId),
  )
  const { startTime, day, duration } = priority.sessions[timetableSessionIndex]
  const startTimeZonedDateTime = calculateEventStartCalendarTimezoneCoerced({
    startTime,
    day,
    calendarTimezoneId,
    eventTimezoneId: preference.classDocument.timezoneId,
    startTemporalZonedDateTime,
  })
  const { t } = useTranslation("preferences")
  const conflicts = usePageSelector((state) =>
    eventsSelectors.selectPreferencePriorityConflicts(state, {
      preferenceId: preference.id,
      priorityId,
    }),
  )

  return (
    <div className="fc-event-main-frame h-full">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className={cn(
              buttonVariants({ variant: "link" }),
              "fc-event-title fc-stick top-0 m-0 h-min w-full justify-start p-0 text-xs text-current ring-current!",
            )}
          >
            {title}
          </TooltipTrigger>
          <TooltipContent className="flex flex-col gap-1 text-white">
            {preference.issues.includes("student_ineligible") && (
              <span className="text-destructive-foreground bg-destructive rounded-md p-1">
                {t("timetable.schedule.invalid")}
              </span>
            )}
            {conflicts.length > 0 ? (
              <span className="text-destructive-foreground bg-destructive rounded-md p-1">
                {t("timetable.schedule.conflicts")}
              </span>
            ) : null}
            <span className="font-bold">
              {t("timetable.schedule.full_rank_details", {
                preference: preference.rank,
                priority: priority.priority,
              })}
            </span>
            <span>
              {t("timetable.schedule.educator_name", {
                name: preference.educatorName,
              })}
            </span>
            <span>
              {t("timetable.schedule.custom_class_name", {
                name: preference.classDocument.name,
              })}
            </span>
            <span>{t("timetable.schedule.duration", { duration })}</span>
            <span>
              {t("timetable.schedule.fixed", {
                day: displayDay(day),
                start: displayPlainTime(startTimeZonedDateTime.toPlainTime()),
                end: displayPlainTime(
                  startTimeZonedDateTime
                    .add({ minutes: duration })
                    .toPlainTime(),
                ),
              })}
            </span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

function PreferenceSheet() {
  const { t } = useTranslation("preferences")
  const dispatch = usePageDispatch()
  const preference = usePageSelector(
    preferencesSelectors.selectSelectedPreference,
  )
  if (!preference) return null
  if (preference.classDocument === null) return null

  const { classDocument } = preference

  return (
    <Card className="w-full max-w-64 overflow-y-auto p-3">
      <button
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
        onClick={() => dispatch(preferencesActions.preferenceSelected(null))}
      >
        <X className="size-4" />
      </button>
      <CardHeader className="p-3">
        <CardTitle>
          {t("preference_sheet.title", { rank: preference.rank })}
        </CardTitle>
        <h2 className="text-lg leading-none font-semibold tracking-tight">
          {t("preference_sheet.class_name", { name: classDocument.name })}
        </h2>
        <Alert>
          {/* <Info className="size-4" /> */}
          <AlertTitle>How it Works</AlertTitle>
          <AlertDescription className="font-normal">
            {t("preference_sheet.description", {
              educatorName: preference.educatorName,
            })}
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 overflow-y-auto p-1 pt-3">
        {preference.classDocument.timetablePriorities.map((priority) => (
          <PrioritySelector
            priorityId={priority}
            selectedPreference={preference}
            key={priority}
          />
        ))}
      </CardContent>
    </Card>
  )
}

function PrioritySelector({
  priorityId,
  selectedPreference,
}: {
  priorityId: PriorityId
  selectedPreference: ValidPreference
}) {
  const dispatch = usePageDispatch()
  const { t } = useTranslation("preferences")
  const priority = usePageSelector((state) =>
    prioritiesSelectors.selectPriority(state, priorityId),
  )
  const errors = usePageSelector((state) =>
    selectPreferenceErrors(state, {
      preferenceId: selectedPreference.id,
      priorityId,
    }),
  )

  const selectPriority = async (priority: Priority) => {
    if (priority.preferenceId === selectedPreference.id) {
      await dispatch(dissociatePriorityWithPreference({ priority }))
    } else {
      await dispatch(
        associatePriorityWithPreference({
          priority,
          preference: selectedPreference,
        }),
      )
    }
  }

  return (
    <Card
      className="flex w-full flex-col gap-2 p-0 font-normal"
      key={priority.uuid}
      onPointerEnter={() =>
        dispatch(eventsActions.priorityHovered(priority.uuid))
      }
      onPointerLeave={() => dispatch(eventsActions.priorityHovered(null))}
    >
      <CardHeader className="p-3 pb-0">
        <CardTitle className={`text-base font-medium`}>
          {t("edit.class.preference.schedule.priority", {
            priority: priority.priority,
          })}
        </CardTitle>
        {priority.preferenceId === null ? null : (
          <AlreadyPreferencedDescription preferenceId={priority.preferenceId} />
        )}
        <Errors errors={errors} />
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <ul>
          {priority.sessions.map(({ day, duration, startTime }, index) => (
            <TimetableSession
              day={day}
              start_time={startTime}
              duration={duration}
              eventTimezoneId={selectedPreference.classDocument.timezoneId}
              key={index}
            />
          ))}
        </ul>
      </CardContent>
      <CardFooter className="px-3 pb-3">
        <Button className="w-full" onClick={() => selectPriority(priority)}>
          {priority.preferenceId === null
            ? t("preference_sheet.priority.select")
            : priority.preferenceId === selectedPreference.id
              ? t("preference_sheet.priority.deselect")
              : t("preference_sheet.priority.move")}
        </Button>
      </CardFooter>
    </Card>
  )
}

const AlreadyPreferencedDescription = ({
  preferenceId,
}: {
  preferenceId: PreferenceId
}) => {
  const { rank } = usePageSelector((state) =>
    preferencesSelectors.selectPreference(state, preferenceId),
  )
  const { t } = useTranslation("preferences")
  return (
    <CardDescription>
      {t("preference_sheet.priority.description", { rank: rank })}
    </CardDescription>
  )
}
